import { useMemo, useState, useCallback } from "react"
import { useFormik } from "formik"
import * as Yup from "yup"
import {
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
  UncontrolledTooltip,
} from "reactstrap"

import TableContainer from "../../../components/Common/TableContainer"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import DeleteModal from "../../../components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

import {
  useChassisCodes,
  useCreateChassisCode,
  useEditChassisCode,
  useDeleteChassisCode,
} from "hooks/chassisCodeHook"
import { useListParams } from "hooks/useListParams"
import { useToastMessage } from "hooks/useToastMessage"
import { formatReadableDate } from "utils/formatDate"
import { useModels } from "hooks/modelHook"
import { modelToModelIdsNumber } from "utils/modelUtils"
import BadgeList from "components/Common/BadgeList"
import ButtonCheckboxList from "components/Common/ButtonCheckboxList"

const ChassisCodeList = () => {
  document.title = "Chassis Code List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [chassisCode, setChassisCode] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({ page: 1, search: "" })

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      with_models: true,
      ...(params.search && { search: params.search }),
    }),
    [params.page, params.search]
  )

  const {
    data: chassisResponse,
    isLoading,
    refetch,
    isRefetching,
  } = useChassisCodes(queryParams)

  const { data: modelsResponse, isLoading: modelsFetchLoading } = useModels(
    { all: true },
    modal
  )

  const { mutateAsync: createCode, isPending: createLoading } =
    useCreateChassisCode()
  const { mutateAsync: editCode, isPending: editLoading } = useEditChassisCode()
  const { mutateAsync: deleteCode, isPending: deleteLoading } =
    useDeleteChassisCode()

  const { handleResponse, handleError } = useToastMessage()

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false)
      setChassisCode(null)
      setIsEdit(false)
    } else setModal(true)
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setChassisCode(null)
    setIsEdit(false)
    toggle()
  }, [toggle])

  const handleChassisClick = useCallback(
    row => {
      setChassisCode({ id: row.id, name: row.name })
      setIsEdit(true)
      toggle()
    },
    [toggle]
  )

  const onClickDelete = useCallback(row => {
    setChassisCode(row)
    setDeleteModal(true)
  }, [])

  const handleDelete = useCallback(async () => {
    if (!chassisCode?.id) return
    try {
      const res = await deleteCode(chassisCode.id)
      handleResponse(res, "Chassis code deleted successfully.")
      setDeleteModal(false)
    } catch (error) {
      handleError(error, "Failed to delete chassis code.")
    }
  }, [chassisCode, deleteCode, handleResponse, handleError])

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        name: values.name,
        modelIds: values.selectedModels?.map(n => Number(n)),
      }
      const action = isEdit
        ? editCode({ id: chassisCode.id, ...payload })
        : createCode(payload)

      try {
        const res = await action
        handleResponse(
          res,
          `Chassis code ${isEdit ? "updated" : "created"} successfully.`
        )
        if (res.success) resetForm()
        toggle()
      } catch (error) {
        handleError(error, "Failed to save chassis code.")
      }
    },
    [
      isEdit,
      chassisCode,
      editCode,
      createCode,
      toggle,
      handleResponse,
      handleError,
    ]
  )

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: chassisCode?.name || "",
      selectedModels: modelToModelIdsNumber(chassisCode?.models) || [],
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        .min(2, "Name must be at least 2 characters")
        .required("Please enter chassis code name"),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      { header: "Name", accessorKey: "name" },
      {
        header: "Models",
        accessorKey: "models",
        cell: ({ row }) => <BadgeList items={row.original.models} />,
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-info"
                onClick={() => handleChassisClick(row.original)}
                id={`edittooltip-${row.original.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`edittooltip-${row.original.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-danger"
                onClick={() => onClickDelete(row.original)}
                id={`deletetooltip-${row.original.id}`}
              >
                <i className="mdi mdi-delete-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`deletetooltip-${row.original.id}`}
                >
                  Delete
                </UncontrolledTooltip>
              </button>
            </li>
          </ul>
        ),
      },
    ],
    [handleChassisClick, onClickDelete]
  )

  const data = chassisResponse?.data?.data || []
  const paginationInfo = chassisResponse?.data

  return (
    <>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Chassis Code" breadcrumbItem="Chassis Codes" />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom d-flex align-items-center">
                  <h5 className="mb-0 flex-grow-1 card-title">Chassis Codes</h5>
                  <div>
                    <Button
                      color="primary"
                      onClick={handleAddNewClick}
                      className="me-1"
                    >
                      Add New Chassis Code
                    </Button>
                    <Button color="light" onClick={refetch} className="me-1">
                      <i
                        className={`mdi mdi-refresh ${
                          isRefetching ? "mdi-spin" : ""
                        }`}
                      />
                    </Button>
                    {hasActiveFilters && (
                      <Button color="danger" onClick={resetParams}>
                        Reset Filters
                      </Button>
                    )}
                  </div>
                </CardBody>

                <CardBody>
                  <Row className="mb-3">
                    <Col sm={4}>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                  </Row>

                  <TableContainer
                    columns={columns}
                    data={data}
                    isPagination
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || isRefetching}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal isOpen={modal} toggle={toggle} centered>
            <ModalHeader toggle={toggle}>
              {isEdit ? "Edit Chassis Code" : "Add Chassis Code"}
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={validation.handleSubmit}>
                <div className="mb-3">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Enter chassis code name"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.name || ""}
                    invalid={
                      validation.touched.name && !!validation.errors.name
                    }
                  />
                  {validation.touched.name && validation.errors.name && (
                    <FormFeedback>{validation.errors.name}</FormFeedback>
                  )}
                </div>

                <div className="mb-3">
                  <Label>Select Models for This Chassis Code</Label>
                  <div className="mb-3">
                    <ButtonCheckboxList
                      items={modelsResponse?.data}
                      name="selectedModels"
                      selected={validation.values.selectedModels}
                      onChange={newSelected =>
                        validation.setFieldValue("selectedModels", newSelected)
                      }
                      loading={modelsFetchLoading}
                    />
                  </div>
                </div>

                <div className="text-end">
                  <Button
                    color="success"
                    type="submit"
                    disabled={createLoading || editLoading}
                  >
                    {isEdit ? "Save Changes" : "Add Chassis Code"}
                    {(createLoading || editLoading) && "..."}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </>
  )
}

export default ChassisCodeList
